<script lang="ts" setup>
  import { computed } from 'vue';
  import { ICON_SIZES, ICON_NAMES, TITLE_VARIANTS, DESCRIPTION_VARIANTS } from './lib';
  import { ToastType, ToastSize, ToastEmit } from './Toast.types';
  import { type IllustrationName, Icon, Illustration, Button, Typo, ButtonAppearance, ButtonSize } from '@/components';

  interface ToastProps {
    title?: string;
    description: string;
    type?: `${ToastType}`;
    size?: `${ToastSize}`;
    illustration?: `${IllustrationName}`;
    inline?: boolean;
    closable?: boolean;
    mobile?: boolean;
    icon?: boolean;
  }

  interface ToastEmits {
    (e: ToastEmit.Close): void;
  }

  const props = withDefaults(defineProps<ToastProps>(), {
    title: '',
    type: ToastType.Success,
    size: ToastSize.S,
    illustration: undefined,
    inline: true,
    closable: false,
    mobile: false,
    icon: true,
  });

  const emit = defineEmits<ToastEmits>();

  const iconName = computed(() => ICON_NAMES[props.type]);
  const iconSize = computed(() => ICON_SIZES[props.size]);

  const closeBtnSize = computed(() => {
    if ([ToastSize.XXL, ToastSize.XL, ToastSize.L].includes(props.size)) {
      return ButtonSize.M;
    }

    return ButtonSize.S;
  });

  const titleVariant = computed(() => TITLE_VARIANTS[props.size]);
  const descriptionVariant = computed(() => DESCRIPTION_VARIANTS[props.size]);

  const closeBtnAppearance = computed(() => {
    if (props.type === ToastType.CriticalError) {
      return ButtonAppearance.SubPrimary;
    }

    return ButtonAppearance.Tertiary;
  });

  const onClose = () => {
    emit(ToastEmit.Close);
  };
</script>

<template>
  <div :class="['tl-toast', `tl-toast_${type}`, `tl-toast_size-${size}`, { 'tl-toast_mobile': mobile }]">
    <div :class="['tl-toast__icon-container', `tl-toast__icon-container_${type}`]">
      <slot name="icon">
        <Illustration
          v-if="illustration"
          data-test-id="illustration"
          :name="illustration"
          class="tl-toast__illustration"
        />
        <Icon v-else-if="icon" data-test-id="icon" :name="iconName" :size="iconSize" class="tl-toast__icon" />
      </slot>
    </div>
    <div data-test-id="content">
      <header v-if="title" class="tl-toast__title">
        <Typo data-test-id="title" :variant="titleVariant">{{ title }}</Typo>
      </header>

      <slot name="content">
        <Typo :variant="descriptionVariant">{{ description }}</Typo>
      </slot>

      <div v-if="$slots.footer" data-test-id="footer" class="tl-toast__footer">
        <slot name="footer" />
      </div>
    </div>

    <Button
      v-if="closable"
      data-test-id="close"
      :appearance="closeBtnAppearance"
      type="icon"
      icon="close"
      :size="closeBtnSize"
      class="tl-toast__close"
      @click="onClose"
    />
  </div>
</template>

<style lang="scss" scoped>
  .tl-toast {
    $root: &;

    align-items: flex-start;
    border: 1px solid;
    border-radius: var(--rounding-m);
    box-sizing: border-box;
    color: var(--color-text-primary);
    display: inline-flex;
    gap: var(--spacing-xs);
    width: 480px;

    &_mobile {
      width: 100%;
    }

    &__icon-container {
      flex-shrink: 0;

      &_success {
        fill: var(--color-green-40);
      }

      &_warning {
        fill: var(--color-orange-40);
      }

      &_error {
        fill: var(--color-rose-40);
      }

      &_critical-error {
        fill: var(--color-neutral-0);
      }

      &_info {
        fill: var(--color-sky-40);
      }

      &_neutral-info {
        fill: var(--color-slate-40);
      }

      &_important-info {
        fill: var(--color-orange-40);
      }

      &_white {
        fill: var(--color-slate-30);
      }
    }

    &_success {
      background-color: var(--color-green-025);
      border-color: var(--color-green-30);
    }

    &_warning {
      background-color: var(--color-yellow-05);
      border-color: var(--color-orange-30);
    }

    &_error {
      background-color: var(--color-rose-025);
      border-color: var(--color-rose-40);
    }

    &_critical-error {
      background-color: var(--color-rose-40);
      border-color: var(--color-red-30);
      color: var(--color-text-inverse-white);
    }

    &_info {
      background-color: var(--color-sky-025);
      border-color: var(--color-sky-30);
    }

    &_neutral-info {
      background-color: var(--color-slate-05);
      border-color: var(--color-slate-30);
    }

    &_important-info {
      background-color: var(--color-orange-025);
      border-color: var(--color-orange-30);
    }

    &_white {
      background-color: var(--color-bg-primary);
      border-color: var(--color-slate-20);
    }

    &_size {
      &-xxs {
        gap: var(--spacing-xxs);
        padding: var(--spacing-xs);

        #{$root}__illustration {
          width: 40px;
        }
      }

      &-xs,
      &-s,
      &-m {
        padding: var(--spacing-s);
      }

      &-xs {
        #{$root}__illustration {
          width: 40px;
        }
      }

      &-s {
        #{$root}__illustration {
          width: 60px;
        }
      }

      &-m {
        #{$root}__illustration {
          width: 80px;
        }
      }

      &-l {
        padding: var(--spacing-m);

        #{$root}__illustration {
          width: 100px;
        }

        #{$root}__title {
          margin-bottom: var(--spacing-xs);
        }
      }

      &-xl,
      &-xxl {
        padding: var(--spacing-l);

        #{$root}__title {
          margin-bottom: var(--spacing-xs);
        }
      }

      &-xl {
        #{$root}__illustration {
          width: 120px;
        }
      }

      &-xxl {
        #{$root}__illustration {
          width: 140px;
        }
      }
    }

    &__title {
      margin-bottom: var(--spacing-xxs);
    }

    &__close {
      margin-left: auto;
    }

    &__footer {
      margin-top: var(--spacing-m);
    }
  }
</style>
